import { useState } from "react";
import React from "react";
import { Swords } from "lucide-react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
interface PricingPlanProps {
  name: string;
  price: string;
  yearlyPrice?: string;
  period?: string;
  description: string;
  features: string[];
  featured?: boolean;
}

// Pricing Section Component
export const PricingSection = ({
  pricingPlans,
  accentText,
  handleSubscribeClick
}: {
  pricingPlans: PricingPlanProps[];
  accentText: (text: string) => React.JSX.Element;
  handleSubscribeClick: () => void;
}) => {
  const [isYearly, setIsYearly] = useState(false);
  return <section id="pricing" className="py-24 md:py-32 relative overflow-hidden" data-sentry-component="PricingSection" data-sentry-source-file="LandingSections.tsx">
            <div className="absolute inset-0 -z-10 opacity-10">
                <div className="absolute top-1/4 -left-60 w-96 h-96 bg-gradient-radial from-sky-600/30 to-transparent rounded-full blur-3xl"></div>
                <div className="absolute bottom-1/4 -right-60 w-96 h-96 bg-gradient-radial from-indigo-600/30 to-transparent rounded-full blur-3xl"></div>
            </div>

            <div className="container mx-auto px-4 sm:px-6 relative z-10">
                <div className="max-w-3xl mx-auto text-center mb-16 md:mb-20">
                    <h2 className="text-[clamp(2.25rem,5vw,3rem)] font-bold leading-tight mb-6 bg-gradient-to-r from-sky-400 via-cyan-400 to-sky-500 text-transparent bg-clip-text">
                        Simple, Transparent Pricing
                    </h2>
                    <p className="text-[clamp(1rem,2.5vw,1.125rem)] text-slate-300 leading-relaxed">
                        Choose the plan that&apos;s right for you
                    </p>
                    <div className="flex items-center justify-center gap-4 mt-10">
                        <span className={cn("text-sm font-medium transition-colors duration-300", !isYearly ? "text-white" : "text-slate-400")}>
                            Monthly
                        </span>
                        <Switch checked={isYearly} onCheckedChange={setIsYearly} aria-label="Yearly Subscription" className="data-[state=checked]:bg-sky-600" data-sentry-element="Switch" data-sentry-source-file="LandingSections.tsx" />
                        <span className={cn("text-sm font-medium transition-colors duration-300", isYearly ? "text-white" : "text-slate-400")}>
                            Yearly
                            <span className="ml-2 text-xs text-emerald-400">
                                (Save up to 20%)
                            </span>
                        </span>
                    </div>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 max-w-7xl mx-auto">
                    {pricingPlans.map((plan, i) => <div key={i} className={cn("relative flex group", plan.featured && "lg:-mt-6 z-10")}>
                            <Card className={cn("flex flex-col w-full rounded-xl overflow-hidden", "bg-slate-900/60 backdrop-blur-lg border border-slate-800/70", "transition-all duration-300 ease-in-out", "hover:bg-slate-800/70 hover:border-slate-700 hover:shadow-xl hover:shadow-slate-900/20", plan.featured && "border-sky-600/80 shadow-lg shadow-sky-800/20 hover:border-sky-500/90")}>
                                <CardContent className="p-6 md:p-8 flex flex-col flex-grow">
                                    <div className="flex flex-col items-center text-center flex-grow">
                                        <h3 className={cn("text-2xl font-semibold mb-2", plan.featured && "bg-gradient-to-r from-sky-400 to-cyan-500 text-transparent bg-clip-text")}>
                                            {plan.name}
                                        </h3>
                                        <p className="text-slate-300/90 mb-6 text-sm leading-relaxed">
                                            {plan.description}
                                        </p>
                                        <div className={cn("mb-8", plan.featured && "scale-105")}>
                                            <span className={cn("text-4xl font-bold", plan.featured && "bg-gradient-to-r from-sky-400 to-cyan-500 text-transparent bg-clip-text")}>
                                                {isYearly ? plan.yearlyPrice : plan.price}
                                            </span>
                                            {plan.period && <span className="text-slate-400 ml-1.5 text-sm">
                                                    {isYearly ? "per year" : plan.period}
                                                </span>}
                                        </div>
                                        <ul className="space-y-3.5 mb-8 text-left w-full flex-grow">
                                            {plan.features.map((feature, j) => <li key={j} className="flex items-start gap-3">
                                                    <Swords className={cn("h-5 w-5 flex-shrink-0 mt-0.5", plan.featured ? "text-sky-500" : "text-cyan-400")} />
                                                    <span className="text-slate-300 text-sm">
                                                        {accentText(feature)}
                                                    </span>
                                                </li>)}
                                        </ul>
                                        <Button className={cn("w-full mt-auto !px-6 !py-2.5 rounded-lg", plan.featured ? "!bg-gradient-to-r !from-sky-600 !to-cyan-600 hover:!from-sky-700 hover:!to-cyan-700 !text-white !shadow-lg !shadow-sky-900/30 transition-all duration-300 ease-in-out transform hover:!scale-105" : "bg-slate-700 hover:bg-slate-600 text-slate-200 hover:text-white transition-all duration-300 ease-in-out")} onClick={handleSubscribeClick}>
                                            {plan.price === "Free" ? "Get Started" : "Subscribe Now"}
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>)}
                </div>
            </div>
        </section>;
};

// CTA Banner Component
export const CTABanner = () => <section className="py-16 md:py-20 bg-gradient-to-r from-slate-900 via-sky-950/70 to-slate-900 relative overflow-hidden border-t border-b border-slate-800/50" data-sentry-component="CTABanner" data-sentry-source-file="LandingSections.tsx">
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
            <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6 md:gap-10 text-center md:text-left">
                <div className="flex-1">
                    <h3 className="text-[clamp(1.5rem,4vw,2rem)] font-bold leading-tight mb-2 text-white">
                        Ready to create your character art?
                    </h3>
                    <p className="text-slate-300 text-[clamp(1rem,2.5vw,1.125rem)] leading-relaxed">
                        Join thousands of Players using CharGen today.
                    </p>
                </div>
                <Link title="Start Creating for Free Now!" href="/generate" prefetch={false} className="mt-4 md:mt-0" data-sentry-element="Link" data-sentry-source-file="LandingSections.tsx">
                    <Button size="lg" className="!px-8 !py-3 text-lg bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white shadow-lg shadow-emerald-900/30 rounded-xl transition-all duration-300 ease-in-out transform hover:scale-105 hover:shadow-emerald-700/40" data-sentry-element="Button" data-sentry-source-file="LandingSections.tsx">
                        Start Creating for Free Now!
                    </Button>
                </Link>
            </div>
        </div>
    </section>;

// Curved Divider Component
export const CurvedDivider = ({
  color,
  flip = false
}: {
  color: string;
  flip?: boolean;
}) => <div className={`w-100 ${flip ? "transform rotate-180" : ""}`} data-sentry-component="CurvedDivider" data-sentry-source-file="LandingSections.tsx">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" data-sentry-element="svg" data-sentry-source-file="LandingSections.tsx">
            <path d="M0 0L60 10C120 20 240 40 360 46.7C480 53 600 47 720 36.7C840 27 960 13 1080 16.3C1200 20 1320 40 1380 50L1440 60V120H0V0Z" fill={color} data-sentry-element="path" data-sentry-source-file="LandingSections.tsx" />
        </svg>
    </div>;